import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

type Distribution = 'uniform' | 'normal' | 'exponential';
type OutFormat = 'lines' | 'csv' | 'json';

interface State {
    min: number;
    max: number;
    integer: boolean;
    distribution: Distribution;
    count: number;
    format: OutFormat;
    output: string;
    copied: boolean;
}

function gauss(mean: number, std: number): number {
    const u = 1 - Math.random();
    const v = Math.random();
    return mean + std * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

export default class RandomNumberComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        min: 1,
        max: 100,
        integer: true,
        distribution: 'uniform',
        count: 10,
        format: 'lines',
        output: '',
        copied: false,
    };

    componentDidMount() {
        this.generate();
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    generate = () => {
        const { min, max, integer, distribution, count, format } = this.state;
        if (max <= min) {
            this.setState({ output: 'Error: max must be greater than min' });
            return;
        }
        const nums: number[] = [];
        const range = max - min;
        const mean = (min + max) / 2;
        const std = range / 6;
        for (let i = 0; i < count; i++) {
            let v: number;
            if (distribution === 'uniform') {
                v = min + Math.random() * range;
            } else if (distribution === 'normal') {
                let attempts = 0;
                do { v = gauss(mean, std); attempts++; } while ((v < min || v > max) && attempts < 50);
                v = Math.min(max, Math.max(min, v));
            } else {
                v = min - Math.log(1 - Math.random()) * (range / 4);
                v = Math.min(max, Math.max(min, v));
            }
            nums.push(integer ? Math.round(v) : Number(v.toFixed(4)));
        }
        let text = '';
        if (format === 'csv') text = nums.join(',');
        else if (format === 'json') text = JSON.stringify(nums);
        else text = nums.join('\n');
        this.setState({ output: text, copied: false });
    };

    copyOutput = () => {
        if (!this.state.output || this.state.output.startsWith('Error')) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { min, max, integer, distribution, count, format, output, copied } = this.state;

        const form = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Range</span></div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="rn-min">Min</label>
                            <input id="rn-min" type="number" inputMode="decimal" value={min}
                                onChange={e => this.setState({ min: +e.target.value || 0 }, this.generate)} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="rn-max">Max</label>
                            <input id="rn-max" type="number" inputMode="decimal" value={max}
                                onChange={e => this.setState({ max: +e.target.value || 100 }, this.generate)} />
                        </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={integer}
                            onChange={e => this.setState({ integer: e.target.checked }, this.generate)} />
                        Integers only (otherwise 4-decimal floats)
                    </label>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Distribution &amp; output</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="rn-dist">Distribution</label>
                        <select id="rn-dist" value={distribution}
                            onChange={e => this.setState({ distribution: e.target.value as Distribution }, this.generate)}>
                            <option value="uniform">Uniform</option>
                            <option value="normal">Normal (Gaussian, mean = midpoint)</option>
                            <option value="exponential">Exponential (decreasing)</option>
                        </select>
                    </div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="rn-count">Count</label>
                            <input id="rn-count" type="number" inputMode="decimal" min={1} max={100000} value={count}
                                onChange={e => this.setState({ count: Math.max(1, Math.min(100000, +e.target.value || 10)) }, this.generate)} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="rn-format">Output format</label>
                            <select id="rn-format" value={format}
                                onChange={e => this.setState({ format: e.target.value as OutFormat }, this.generate)}>
                                <option value="lines">One per line</option>
                                <option value="csv">CSV (comma-separated)</option>
                                <option value="json">JSON array</option>
                            </select>
                        </div>
                    </div>
                    <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.generate} style={{ alignSelf: 'flex-start' }}>
                        ↻ Regenerate
                    </button>
                </div>
            </>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated numbers"
                previewMeta={`${count} number${count === 1 ? '' : 's'} · ${format}`}
                preview={<pre className={genStyles.previewArea} aria-label="Generated random numbers">{output}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy'}
                    </button>
                }
            />
        );
    }
}
